<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { useVModel } from '@vueuse/core'
import { cn } from '@/lib/utils'
import { vAutoResize } from '@/utils/autoResize'
import { vAutoAnimate } from '@formkit/auto-animate'

const props = defineProps<{
  class?: HTMLAttributes['class']
  defaultValue?: string | number
  modelValue?: string | number
  rows?: number
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | number): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
  passive: true,
  defaultValue: props.defaultValue,
})
</script>

<template>
  <textarea 
    v-model="modelValue" 
    v-auto-resize
    :rows="props.rows"
    :class="cn(
      // Base styles
      'flex min-h-20 w-full rounded-md px-3 py-2 text-sm',
      'bg-background text-foreground',
      'resize-none', // Disable manual resize since we have auto-resize
      // Border styles - more prominent
      'border-2 border-input/60',
      'transition-all duration-200 ease-in-out',
      // Focus styles - prominent primary colors
      'focus:border-primary focus:outline-none',
      'focus:ring-2 focus:ring-primary/20',
      'focus:ring-offset-1 focus:ring-offset-background',
      // Hover styles
      'hover:border-input',
      // Placeholder styles
      'placeholder:text-muted-foreground/60',
      // Disabled styles
      'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-muted/50',
      // Error states (can be enhanced later)
      'invalid:border-destructive invalid:ring-destructive/20',
      props.class
    )" 
  />
</template>